import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { format } from 'date-fns'
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import { useAuth } from '../../../../app/modules/auth'
import { useAppData } from '../../../../app/providers/AppDataProvider'
import { HeaderUserMenu } from '../../../partials/layout/header-menus/HeaderUserMenu'
import {
  UnifiedCalendarItem,
  CALENDAR_ITEM_COLORS,
} from '../../../../app/types/calendar'
import { useTodayItems } from '../../../../app/hooks/useTodayItems'
import { db } from '../../../../firebase'
import { CalendarEvent } from '../../../../app/pages/job-summary/Interfaces'
import { XeroInvoice } from '../../../../app/types/xero'
import NewEventModal from '../../../../app/pages/dashboard/calendar/NewEventModal'
import InvoiceQuickViewModal from '../../../../app/pages/dashboard/calendar/InvoiceQuickViewModal'

// ─── helpers ─────────────────────────────────────────────────────────────────

function getFirstName(currentUser: any): string {
  if (!currentUser) return ''
  if (currentUser.firstName) return currentUser.firstName
  if (currentUser.name) return currentUser.name.split(' ')[0]
  if (currentUser.email) return currentUser.email.split('@')[0]
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}')
    return u.firstName || u.name?.split(' ')[0] || ''
  } catch { return '' }
}

function getUid(currentUser: any): string {
  return (
    currentUser?.id ||
    currentUser?.uid ||
    getAuth().currentUser?.uid ||
    ''
  )
}

function todayDateKey(): string {
  return format(new Date(), 'yyyy-MM-dd')
}

function formatItemTime(item: UnifiedCalendarItem): string {
  if (item.allDay) return 'All day'
  try {
    return new Date(item.start).toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  } catch { return '' }
}

function formatAmount(amount: number, currency = 'AUD'): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

// ─── type display config ──────────────────────────────────────────────────────

const TYPE_CONFIG: Record<string, { color: string; label: string }> = {
  event:             { color: CALENDAR_ITEM_COLORS.event,             label: 'Event' },
  invoice_due:       { color: CALENDAR_ITEM_COLORS.invoice_due,       label: 'Invoice Due' },
  invoice_created:   { color: CALENDAR_ITEM_COLORS.invoice_created,   label: 'Invoice' },
  recurring_invoice: { color: CALENDAR_ITEM_COLORS.recurring_invoice, label: 'Recurring' },
  recurring_expense: { color: CALENDAR_ITEM_COLORS.recurring_expense, label: 'Expense' },
  bill_due:          { color: CALENDAR_ITEM_COLORS.bill_due,          label: 'Bill Due' },
}

// ─── component ────────────────────────────────────────────────────────────────

const AsideToolbar = () => {
  const { currentUser } = useAuth()
  const appData = useAppData()

  const firstName = getFirstName(currentUser)
  const today = new Date()

  // ── Modal state ────────────────────────────────────────────────────────────
  const [showEventModal, setShowEventModal] = useState(false)
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [selectedInvoice, setSelectedInvoice] = useState<XeroInvoice | null>(null)
  const [selectedInvoiceViewType, setSelectedInvoiceViewType] =
    useState<'invoice_due' | 'invoice_created'>('invoice_due')

  // ── Checklist state ────────────────────────────────────────────────────────
  // Shared across all users: { checkedBy: { [itemId]: firstName } }
  const [checkedBy, setCheckedBy] = useState<Record<string, string>>({})

  // Subscribe to today's shared checklist — real-time so all users see updates instantly
  useEffect(() => {
    const uid = getUid(currentUser)
    if (!uid) return
    const dateKey = todayDateKey()

    const unsub = onSnapshot(
      doc(db, 'checklist', dateKey),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() as { checkedBy?: Record<string, string> }
          setCheckedBy(data.checkedBy || {})
        } else {
          setCheckedBy({})
        }
      },
      () => { /* ignore errors */ }
    )

    return () => unsub()
  }, [currentUser])

  // Persist the full checkedBy map to the shared Firestore document
  const saveChecklist = useCallback((nextMap: Record<string, string>) => {
    const dateKey = todayDateKey()
    setDoc(
      doc(db, 'checklist', dateKey),
      { checkedBy: nextMap },
      { merge: false }
    ).catch(() => { /* ignore */ })
  }, [])

  const toggleCheck = useCallback((itemId: string) => {
    const uid = getUid(currentUser)
    if (!uid) return
    setCheckedBy((prev) => {
      const next = { ...prev }
      if (next[itemId]) {
        delete next[itemId]
      } else {
        next[itemId] = firstName || 'Someone'
      }
      saveChecklist(next)
      return next
    })
  }, [currentUser, firstName, saveChecklist])

  // ── Build today's items (manually created events only) ────────────────────
  // Sourced from the shared `useTodayItems` composer rather than re-derived
  // here. The local version expanded no recurrence (so a weekly stand-up never
  // appeared) and filtered no soft-deleted events (so once the calendar moved to
  // `status: 'cancelled'` deletes, cancelled events would have kept showing).
  // This app had five separate "what's on my plate today" composers giving five
  // different answers depending on which theme was active.
  const { today: todayItems } = useTodayItems({
    days: 0,
    filters: {
      showEvents: true,
      showInvoices: false,
      showRecurringInvoices: false,
      showRecurringExpenses: false,
      showJobs: false,
    },
  })

  const uncheckedCount = todayItems.filter((i) => !checkedBy[i.id]).length

  // ── Item click ─────────────────────────────────────────────────────────────
  const handleItemClick = useCallback(async (item: UnifiedCalendarItem) => {
    switch (item.type) {
      case 'event':
        if (item.eventData?.id) {
          try {
            const snap = await getDoc(doc(db, 'events', item.eventData.id))
            if (snap.exists()) {
              setSelectedEvent({ ...snap.data() as CalendarEvent, id: item.eventData.id })
              setShowEventModal(true)
            }
          } catch { /* ignore */ }
        }
        break
      case 'invoice_due':
      case 'invoice_created':
        if (item.invoiceData) {
          setSelectedInvoice(item.invoiceData)
          setSelectedInvoiceViewType(item.type)
          setShowInvoiceModal(true)
        }
        break
      case 'bill_due':
        if (item.invoiceData) {
          setSelectedInvoice(item.invoiceData)
          setSelectedInvoiceViewType('invoice_due')
          setShowInvoiceModal(true)
        }
        break
    }
  }, [])

  const handleEventAdded = useCallback(() => {
    appData.refreshEvents?.()
  }, [appData])

  // ─── render ───────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── User greeting ──────────────────────────────────────────────── */}
      <div
        className='aside-user d-flex align-items-center py-4 px-4'
        style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div className='flex-grow-1'>
          <a
            data-kt-menu-trigger='click'
            data-kt-menu-placement='bottom-start'
            data-kt-menu-overflow='false'
            href='#'
            onClick={(e) => e.preventDefault()}
            className='text-white text-hover-primary fw-bold fs-5 d-flex align-items-center gap-2'
          >
            {firstName ? `Hi ${firstName}` : 'My Account'}
            <i className='bi bi-chevron-down fs-8 text-gray-500' />
            <HeaderUserMenu />
          </a>
          <div className='text-gray-500 fs-8 mt-1'>
            {format(today, 'EEEE, do MMMM yyyy')}
          </div>
        </div>
      </div>

      {/* ── Today's schedule ────────────────────────────────────────────── */}
      <div className='px-4 pt-5 pb-2'>
        {/* Header row */}
        <div className='d-flex align-items-center justify-content-between mb-3'>
          <span className='text-gray-400 fw-bold fs-8 text-uppercase ls-1'>
            Today's Schedule
          </span>
          {todayItems.length > 0 && (
            <span
              className='fs-9 fw-semibold'
              style={{ color: uncheckedCount === 0 ? '#50cd89' : '#a1a5b7' }}
              title={`${uncheckedCount} remaining`}
            >
              {uncheckedCount === 0
                ? <><i className='bi bi-check-all me-1' />All done</>
                : `${todayItems.length - uncheckedCount}/${todayItems.length}`}
            </span>
          )}
        </div>

        {/* Item list */}
        {todayItems.length === 0 ? (
          <div className='text-center py-4'>
            <i className='bi bi-calendar-check fs-2x text-gray-600 d-block mb-2' />
            <span className='text-gray-500 fs-8'>Nothing scheduled today</span>
          </div>
        ) : (
          <div className='d-flex flex-column gap-2'>
            {todayItems.map((item) => {
              const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.event
              const eventColor = item.type === 'event' ? (item.color || cfg.color) : cfg.color
              const isChecked = !!checkedBy[item.id]
              const completedByName = checkedBy[item.id] || ''

              return (
                <div
                  key={item.id}
                  className='d-flex align-items-start gap-2'
                  style={{
                    background: isChecked ? 'rgba(80,205,137,0.05)' : 'rgba(255,255,255,0.05)',
                    borderRadius: '8px',
                    padding: '8px 10px',
                    transition: 'background 0.2s',
                    border: isChecked ? '1px solid rgba(80,205,137,0.15)' : '1px solid transparent',
                  }}
                >
                  {/* ── Checkbox toggle ── */}
                  <div
                    role='checkbox'
                    aria-checked={isChecked}
                    tabIndex={0}
                    className='flex-shrink-0'
                    style={{
                      cursor: 'pointer',
                      color: isChecked ? '#50cd89' : '#4a4a6a',
                      marginTop: 2,
                      lineHeight: 1,
                      userSelect: 'none',
                      transition: 'color 0.2s',
                    }}
                    onClick={(e) => { e.stopPropagation(); toggleCheck(item.id) }}
                    onKeyDown={(e) => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault(); toggleCheck(item.id)
                      }
                    }}
                    title={isChecked ? 'Mark incomplete' : 'Mark complete'}
                  >
                    <i
                      className={`bi ${isChecked ? 'bi-check-circle-fill' : 'bi-circle'} fs-5`}
                    />
                  </div>

                  {/* ── Main content (opens modal) ── */}
                  <div
                    className='flex-grow-1 min-w-0'
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleItemClick(item)}
                  >
                    {/* Type dot + title */}
                    <div className='d-flex align-items-center gap-2 min-w-0'>
                      <span
                        className='flex-shrink-0'
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          background: eventColor,
                          display: 'inline-block',
                          flexShrink: 0,
                          opacity: isChecked ? 0.5 : 1,
                        }}
                      />
                      <span
                        className='fw-semibold fs-8 text-truncate'
                        style={{
                          color: isChecked ? '#6d7588' : '#ffffff',
                          textDecoration: isChecked ? 'line-through' : 'none',
                          transition: 'color 0.2s, text-decoration 0.15s',
                        }}
                        title={item.title}
                      >
                        {item.title}
                      </span>
                    </div>

                    {/* Meta row */}
                    <div className='d-flex align-items-center gap-2 mt-1' style={{ paddingLeft: 15 }}>
                      <span className='text-gray-500 fs-9'>{cfg.label}</span>
                      {item.amount !== undefined && item.amount > 0 ? (
                        <span
                          className='fs-9 fw-semibold'
                          style={{ color: isChecked ? '#6d7588' : eventColor }}
                        >
                          {(item.type === 'recurring_expense' || item.type === 'bill_due') ? '−' : ''}
                          {formatAmount(item.amount, item.currencyCode)}
                        </span>
                      ) : (
                        <span className='text-gray-600 fs-9'>{formatItemTime(item)}</span>
                      )}
                    </div>

                    {/* Completed-by badge */}
                    {isChecked && completedByName && (
                      <div
                        className='d-flex align-items-center gap-1 mt-1'
                        style={{ paddingLeft: 15 }}
                      >
                        <i className='bi bi-check2 fs-9' style={{ color: '#50cd89' }} />
                        <span className='fs-9' style={{ color: '#50cd89' }}>
                          Completed by {completedByName}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* ── Open chevron ── */}
                  <i
                    className='bi bi-chevron-right fs-9 text-gray-600 flex-shrink-0'
                    style={{ cursor: 'pointer', marginTop: 4 }}
                    onClick={() => handleItemClick(item)}
                  />
                </div>
              )
            })}
          </div>
        )}

        {/* View calendar link — centered */}
        <div className='mt-4 mb-1 d-flex justify-content-center'>
          <button
            type='button'
            className='d-flex align-items-center gap-2 text-gray-400 text-hover-primary fs-8 border-0 bg-transparent'
            style={{ cursor: 'pointer', padding: 0 }}
            onClick={() => window.dispatchEvent(new CustomEvent('open-calendar-modal'))}
          >
            <i className='bi bi-calendar3 fs-7' />
            <span>View full calendar</span>
            <i className='bi bi-arrow-right fs-9' />
          </button>
        </div>
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────── */}
      <NewEventModal
        isOpen={showEventModal}
        onNewEventAdded={handleEventAdded}
        onClose={() => { setShowEventModal(false); setSelectedEvent(null) }}
        event={selectedEvent}
        onEventDeleted={handleEventAdded}
      />

      <InvoiceQuickViewModal
        isOpen={showInvoiceModal}
        onClose={() => { setShowInvoiceModal(false); setSelectedInvoice(null) }}
        invoice={selectedInvoice}
        viewType={selectedInvoiceViewType}
      />
    </>
  )
}

export { AsideToolbar }
